import { Flex } from '@sequencehq/core-components'
import { ConfigurationManagementProps } from 'modules/Integrations/domain'
import invariant from 'tiny-invariant'
import { useState, useEffect, useCallback, useMemo } from 'react'
import { NumeralIntegrationConfig } from 'Integrations/integrationsConfig/numeral/numeral.integration.config'
import NumeralAuthenticationCard from './cards/NumeralAuthenticationCard'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { useQuery } from '@sequencehq/api/utils'

export const NumeralConfigurationManagement = ({
  updateValidationState,
  updateConfiguration,
  integration
}: ConfigurationManagementProps) => {
  invariant(integration.id === 'Numeral', 'Integration is not Numeral')

  const credentialsQuery = useQuery(
    dashboardv99990101Client.getStoredNumeralCredentials
  )

  const numeralConfiguration = useMemo(() => {
    if (credentialsQuery.data) {
      return {
        obfuscatedApiKey: credentialsQuery.data.obfuscatedApiKey
      } as NumeralIntegrationConfig
    }

    return (
      (integration.configuration as NumeralIntegrationConfig) || {
        obfuscatedApiKey: ''
      }
    )
  }, [credentialsQuery.data, integration.configuration])

  const [wasConnectionSuccessful, setWasConnectionSuccessful] = useState(false)
  const [pendingCredentials, setPendingCredentials] = useState<{
    apiKey: string
  } | null>(null)

  const processCredentials = useCallback(
    (credentials: { apiKey: string }) => {
      const newConfiguration: NumeralIntegrationConfig = {
        apiKey: credentials.apiKey,
        obfuscatedApiKey: numeralConfiguration.obfuscatedApiKey || ''
      }
      updateConfiguration(newConfiguration)
      updateValidationState(true)
    },
    [
      updateConfiguration,
      updateValidationState,
      numeralConfiguration.obfuscatedApiKey
    ]
  )

  useEffect(() => {
    if (pendingCredentials) {
      processCredentials(pendingCredentials)
      setPendingCredentials(null)
    }
  }, [pendingCredentials, processCredentials])

  const updateCredentials = useCallback((credentials: { apiKey: string }) => {
    setWasConnectionSuccessful(true)
    setPendingCredentials(credentials)
  }, [])

  const resetValidationState = useCallback(() => {
    if (wasConnectionSuccessful) {
      updateValidationState(false)
      setWasConnectionSuccessful(false)
    }
  }, [wasConnectionSuccessful, updateValidationState])

  const existingCredentials = useMemo(
    () => ({
      apiKey: numeralConfiguration.obfuscatedApiKey
    }),
    [numeralConfiguration.obfuscatedApiKey]
  )

  return (
    <Flex grow={1} width="100%">
      <Flex flexDirection="column" gap="20px" grow={1}>
        <NumeralAuthenticationCard
          onConnectionSuccess={updateCredentials}
          onCredentialsChange={resetValidationState}
          existingCredentials={existingCredentials}
        />
      </Flex>
    </Flex>
  )
}
